// Pure check-in alarm model for the client (architecture sprint-14 §8.2). The server owns the slots; the client
// rings on the server-projected clock: serverNow at receipt + the monotonic delta (the sprint 4 serverNowMs rule,
// clamped at 0 so a tick older than the snapshot never moves time backwards).
import type { AlarmStateDto } from '@shared/alarm-rules';

export interface AlarmSnapshot {
  alarm: AlarmStateDto | null;
  /** performance.now() when the response arrived. */
  receivedAt: number;
}

/** The local acknowledgement: every key <= upTo of this shift is confirmed (sent once the server agreed). */
export interface AlarmAck {
  shiftId: string;
  upTo: string;
  sent: boolean;
}

/** Earliest ring start per scheduled key (ISO → ISO), for the server's ring-drift column. */
export type RangMap = Record<string, string>;

export function alarmServerNowMs(s: { alarm: { serverNow: string }; receivedAt: number }, perfNow: number): number {
  return Date.parse(s.alarm.serverNow) + Math.max(0, perfNow - s.receivedAt);
}

/** Keys that should ring now: open slots plus upcoming marks already reached, minus the acknowledged ones. Sorted. */
export function ringingKeys(alarm: AlarmStateDto, serverNowMs: number, ack: AlarmAck | null): string[] {
  const keys = new Set<string>(alarm.openSlots.map((s) => s.scheduledAt));
  for (const m of alarm.upcomingMarks) if (Date.parse(m) <= serverNowMs) keys.add(m);
  const ackMs = ack && ack.shiftId === alarm.shiftId ? Date.parse(ack.upTo) : Number.NEGATIVE_INFINITY;
  return [...keys].filter((k) => Date.parse(k) > ackMs).sort((a, b) => Date.parse(a) - Date.parse(b));
}

/** Merge two acks of the same shift (keep the later upTo; sent only if the later one was sent). A new shift wins. */
export function mergeAck(current: AlarmAck | null, incoming: AlarmAck): AlarmAck {
  if (!current || current.shiftId !== incoming.shiftId) return incoming;
  const c = Date.parse(current.upTo);
  const i = Date.parse(incoming.upTo);
  if (i > c) return incoming;
  if (i < c) return current;
  return { ...current, sent: current.sent || incoming.sent };
}

/**
 * The ring start to report for a key: the scheduled start of a pre-scheduled source that was running (it rang
 * on time even in a hidden tab), else the projected instant the key was first seen ringing. Keeps the earliest.
 */
export function recordRang(rang: RangMap, key: string, at: string): RangMap {
  const prev = rang[key];
  if (prev !== undefined && Date.parse(prev) <= Date.parse(at)) return rang;
  return { ...rang, [key]: at };
}

/** The confirm body's `rang` list for the keys being confirmed (max 20, newest kept). */
export function rangPayload(rang: RangMap, keys: readonly string[]): Array<{ scheduledAt: string; rangAt: string }> {
  const MAX = 20;
  return keys
    .flatMap((k) => (rang[k] ? [{ scheduledAt: k, rangAt: rang[k] as string }] : []))
    .slice(-MAX);
}

/** Retry delays after a failed confirm (§8.2): 2 s, 5 s, 15 s, then every 15 s. */
export function confirmRetryDelayMs(attempt: number): number {
  const DELAYS = [2_000, 5_000, 15_000];
  return DELAYS[attempt] ?? 15_000;
}

/** Seconds until the next mark on the projected clock, or null. */
export function secondsUntil(iso: string | null, serverNowMs: number): number | null {
  return iso === null ? null : (Date.parse(iso) - serverNowMs) / 1000;
}
